import type {
  CreateReportPayload,
  CreateReportProfilePayload,
  CreateReportSectionPayload,
  ReportTemplate,
  ReportTemplateDefaults,
} from 'api/analytics-reports/types';
import { mapCreateReportPeriodPreset } from 'app/modules/analytics/modules/reports/utils/mapCreateReportPeriodPreset';
import type { CreateReportFormValues } from 'app/modules/analytics/modules/reports/modules/createReport/CreateReportFormProvider';
import type {
  ReportProfileEditorState,
  ReportSectionMetricsContext,
} from 'app/modules/analytics/modules/reports/types/ReportProfileEditorState';
import {
  hasAnyProfileCardNotes,
  profileCardsDifferFromTemplate,
  resolveCardSectionMetrics,
  sortedProfileCards,
} from 'app/modules/analytics/modules/reports/types/ReportProfileEditorState';
import type { PageData, ProfileGroup } from 'types/Page';
import { isReportContentPostsEnabled } from 'app/modules/analytics/modules/reports/constants/reportContentPostSettings';

export type ReportEditorMode = 'create' | 'edit';

export interface BuildCreateReportPayloadInput {
  values: CreateReportFormValues;
  templateCode: string | null;
  selectedTemplate: ReportTemplate | null;
  profileCards: ReportProfileEditorState[];
  sectionMetricsContext: ReportSectionMetricsContext;
  selectedPage: PageData | null;
  selectedProfileGroup: ProfileGroup | null;
  isGroupSelected: boolean;
  mode?: ReportEditorMode;
}

function buildSections(
  performanceMetrics: string[],
  contentMetrics: string[],
  performanceNote: string,
  contentNote: string,
  contentDefaults: Pick<
    ReportTemplateDefaults,
    'content_order_by_posts' | 'content_order_by_reels' | 'content_post_count'
  >,
): CreateReportSectionPayload[] {
  const trimmedPerformanceNote = performanceNote.trim();
  const trimmedContentNote = contentNote.trim();
  const postsEnabled = isReportContentPostsEnabled(contentDefaults);

  const sections: CreateReportSectionPayload[] = [];

  if (performanceMetrics.length > 0) {
    sections.push({
      kind: 'performance',
      metrics: performanceMetrics,
      note: trimmedPerformanceNote || null,
    });
  }

  if (contentMetrics.length > 0 || postsEnabled) {
    sections.push({
      kind: 'content',
      metrics: contentMetrics,
      note: trimmedContentNote || null,
      content_order_by_posts: postsEnabled
        ? contentDefaults.content_order_by_posts ?? null
        : null,
      content_order_by_reels: postsEnabled
        ? contentDefaults.content_order_by_reels ?? null
        : null,
      content_post_count: postsEnabled
        ? contentDefaults.content_post_count ?? null
        : null,
    });
  }

  return sections;
}

export function buildProfilesFromCards(
  profileCards: ReportProfileEditorState[],
  sectionMetricsContext: ReportSectionMetricsContext,
): CreateReportProfilePayload[] {
  return sortedProfileCards(profileCards).map((card, index) => ({
    type: card.type,
    order_column: index,
    page_id: card.type === 'profile' ? card.pageId : null,
    platform: card.platform,
    is_collapsed: card.isCollapsed,
    sections: buildSections(
      resolveCardSectionMetrics(card, 'performance'),
      resolveCardSectionMetrics(card, 'content'),
      card.performanceNote,
      card.contentNote,
      card.contentDefaults,
    ),
  }));
}

function buildProfilesFromCardsInternal(
  profileCards: ReportProfileEditorState[],
  sectionMetricsContext: ReportSectionMetricsContext,
): CreateReportProfilePayload[] {
  return buildProfilesFromCards(profileCards, sectionMetricsContext);
}

export function buildCreateReportPayload(
  input: BuildCreateReportPayloadInput,
): CreateReportPayload {
  const periodPreset = mapCreateReportPeriodPreset(input.values.datePreset);
  const payload: CreateReportPayload = {
    name: input.values.name.trim(),
    period_preset: periodPreset,
  };

  if (periodPreset === 'custom') {
    payload.period_from = input.values.period.start.format('YYYY-MM-DD');
    payload.period_to = input.values.period.end.format('YYYY-MM-DD');
  }

  const canUseTemplateShortcut =
    input.mode !== 'edit' &&
    input.templateCode !== null &&
    input.selectedTemplate !== null &&
    input.isGroupSelected &&
    input.selectedProfileGroup !== null &&
    !hasAnyProfileCardNotes(input.profileCards) &&
    !profileCardsDifferFromTemplate(input.profileCards, input.selectedTemplate);

  if (canUseTemplateShortcut && input.templateCode) {
    payload.template = input.templateCode;
    payload.preset_id = input.selectedProfileGroup!.id;
    return payload;
  }

  payload.profiles = buildProfilesFromCardsInternal(
    input.profileCards,
    input.sectionMetricsContext,
  );

  if (input.templateCode) {
    payload.template = input.templateCode;
  }

  if (input.isGroupSelected && input.selectedProfileGroup) {
    payload.preset_id = input.selectedProfileGroup.id;
  }

  return payload;
}
